import { useState } from 'react';
import type { ReactNode } from 'react';
import { ClickAwayListener, Popper as MuiPopper, styled } from '@mui/material';
import type { PopperProps as MuiPopperProps } from '@mui/material';
import { Paper } from '../Paper';
import type { PaperProps } from '../Paper';
import { darkModeBackgroundImageElevation } from '../../utils/darkModeBackgroundImageElevation';

type PopperProps = MuiPopperProps & { arrow?: boolean };
const Popper = styled(MuiPopper, {
    shouldForwardProp: (prop) => prop !== 'arrow',
})<PopperProps>(({ theme }) => ({
    zIndex: 99,
    padding: 8,
    maxWidth: '100%',
    boxSizing: 'border-box',
    '& .MuiPaper-root': {
        border: `1px solid ${theme.bino.color.lightGray}`,
        overflowX: 'auto',
    },
    '& > div': {
        position: 'relative',
    },
    '&[data-popper-placement*="top"] .MuiPopper-arrow': {
        bottom: '-1em',
        '&::before': {
            bottom: 1,
            marginLeft: '0.707em',
            transform: `rotate(${180 - 45}deg)`,
        },
    },
    '&[data-popper-placement*="bottom"] .MuiPopper-arrow': {
        top: 0,
        '&::before': {
            top: 1,
            marginLeft: '-0.707em',
            transform: `rotate(${0 - 45}deg)`,
        },
    },
    '&[data-popper-placement*="left"] .MuiPopper-arrow': {
        right: '-1em',
        '&::before': {
            right: 1,
            marginTop: '-0.707em',
            transform: `rotate(${90 - 45}deg)`,
        },
    },
    '&[data-popper-placement*="right"] .MuiPopper-arrow': {
        left: 0,
        '&::before': {
            left: 1,
            marginTop: '0.707em',
            transform: `rotate(${270 - 45}deg)`,
        },
    },
}));

const Arrow = styled('div')(({ theme }) => ({
    '&::before': {
        // This is the style for the bottom placement (arrow up), the other needs to adjust rotation
        content: '""',
        position: 'absolute',
        width: '1em',
        height: '1em',
        backgroundColor: theme.palette.background.paper,
        backgroundImage: darkModeBackgroundImageElevation(theme, 1),
        boxSizing: 'border-box',
        border: '1px solid transparent',
        borderTopColor: theme.bino.color.lightGray,
        borderRightColor: theme.bino.color.lightGray,
        boxShadow: '1px -1px 2px 0 rgba(150, 150, 150, 0.1)',
        transformOrigin: '0 0',
    },
}));

// see https://popper.js.org/docs/v2/modifiers/offset/
type PopperOffsetSkidding = number;
type PopperOffsetDistance = number;

export interface StyledPopperProps extends Omit<PopperProps, 'children'> {
    children: ReactNode;
    onClose?: () => void;
    flipEnabled?: boolean;
    paperClasses?: PaperProps['classes'];
    paperProps?: PaperProps;
    offset?: [PopperOffsetSkidding, PopperOffsetDistance];
}

export const StyledPopper = (props: StyledPopperProps) => {
    const {
        open,
        onClose,
        anchorEl,
        arrow = true,
        children,
        placement = 'bottom',
        flipEnabled = true,
        paperClasses = {},
        paperProps,
        offset,
        ...popperProps
    } = props;

    const [arrowEl, setArrowEl] = useState<HTMLDivElement>();

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement={placement}
            arrow={arrow}
            modifiers={[
                {
                    name: 'flip',
                    enabled: flipEnabled,
                    options: {
                        // altBoundary: true,
                        // rootBoundary: 'document',
                        padding: 8,
                    },
                },
                {
                    name: 'arrow',
                    enabled: arrow,
                    options: {
                        element: arrowEl,
                    },
                },
                {
                    name: 'offset',
                    enabled: Boolean(offset),
                    options: {
                        offset,
                    },
                },
            ]}
            {...popperProps}
        >
            <ClickAwayListener
                onClickAway={(event) => {
                    if (onClose) {
                        event.preventDefault();
                        onClose();
                    }
                }}
            >
                <div>
                    {arrow && (
                        <Arrow ref={setArrowEl} className="MuiPopper-arrow" />
                    )}
                    <Paper classes={paperClasses} {...paperProps}>
                        {children}
                    </Paper>
                </div>
            </ClickAwayListener>
        </Popper>
    );
};

export default StyledPopper;
